import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { storyCharactersWithoutCast } from 'fym-shared/src/music24/directedCastRules';
import type { DirectedStory } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import {
  usePickDirectedStoryMutation,
  useWriteDirectedStoriesMutation,
  useWriteDirectedStylesMutation,
} from '../directedVideoApi';
import { storyMessages as messages } from '../messages/storyMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { AgainBox } from './director/AgainBox';
import { choicesKey, initialChoiceId, initialDrafts, sameStory } from './director/choiceRules';
import { DirectedSummary } from './director/DirectedSummary';
import { DirectorNotices } from './director/DirectorNotices';
import { useDirectedAction } from './director/useDirectedAction';
import { DirectorWorking } from './DirectorWorking';
import { StoryCard } from './StoryCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The choices of one director run. A new run gives a new key, so the edits of the old ideas go. */
const StoryChoicesComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, catalogItemUuid, video, song, screen, currentScreen, readOnly, openStep } =
    useDirectedVideoContext();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: video.uuid }),
    [accountId, catalogItemUuid, video.uuid]
  );
  const stories = useMemo(() => video.stories ?? [], [video.stories]);
  const [pickStory] = usePickDirectedStoryMutation();
  const [writeStyles] = useWriteDirectedStylesMutation();
  const [writeStories] = useWriteDirectedStoriesMutation();
  const action = useDirectedAction();
  const [selectedId, setSelectedId] = useState(() => initialChoiceId(stories, video.story));
  const [drafts, setDrafts] = useState<Record<string, DirectedStory>>(() =>
    initialDrafts(stories, video.story, sameStory)
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAsking, setIsAsking] = useState(false);

  const isWorking = video.directorState === 'working';
  const noRuns = video.directorRunsLeft <= 0;
  const busy = readOnly || isWorking || action.isRunning;
  const isRevisit = screen !== currentScreen;
  const shownOf = (story: DirectedStory) => drafts[story.id] ?? story;
  const original = stories.find((story) => story.id === selectedId);
  const picked = original === undefined ? null : shownOf(original);
  const isUnchanged = picked !== null && video.story !== null && sameStory(picked, video.story);
  const hasLaterSteps = video.styles !== null || video.style !== null || video.script !== null;

  const writeAgain = (note?: string) => {
    action.run(() => writeStories({ ...args, note }).unwrap()).catch(console.error);
  };

  const applyStory = async () => {
    if (picked === null) {
      return;
    }
    setIsAsking(false);
    if (isUnchanged && hasLaterSteps) {
      openStep(null);
      return;
    }
    // A story with a recurring person who has no cast picture goes back to the cast first: the
    // artist adds a picture or makes one, then goes on to the style (fix 2026-10-18).
    const needsPictures = storyCharactersWithoutCast(picked, video.cast).length > 0;
    const ok = await action.run(async () => {
      await pickStory({ ...args, story: picked }).unwrap();
      if (!needsPictures) {
        await writeStyles(args).unwrap();
      }
    });
    if (ok && needsPictures) {
      openStep('cast');
    } else if (ok && isRevisit) {
      openStep(null);
    }
  };

  const onUse = () => {
    if (picked !== null && !isUnchanged && hasLaterSteps) {
      setIsAsking(true);
      return;
    }
    applyStory().catch(console.error);
  };

  const isEditingAny = editingId !== null;
  let body: React.ReactNode;
  if (isWorking) {
    body = <DirectorWorking kind="stories" withSkeletons />;
  } else if (stories.length === 0) {
    body = (
      <div className={styles.work}>
        <p className={styles.workText}>{intl.formatMessage(messages.noStories)}</p>
        <SMLButton variant="primary" disabled={busy || noRuns} onClick={() => writeAgain()}>
          {intl.formatMessage(messages.writeStories)}
        </SMLButton>
      </div>
    );
  } else {
    body = (
      <>
        <div className={clsx(styles.choices, isEditingAny && styles.choicesEdit)}>
          {stories.map((story, index) => {
            const shown = shownOf(story);
            return (
              <StoryCard
                key={story.id}
                story={shown}
                number={index + 1}
                sections={song.sections}
                isOn={story.id === selectedId}
                isDim={isEditingAny && editingId !== story.id}
                isEditing={editingId === story.id}
                isEdited={!sameStory(shown, story)}
                disabled={busy}
                onPick={() => setSelectedId(story.id)}
                onEdit={() => {
                  setSelectedId(story.id);
                  setEditingId(story.id);
                }}
                onChange={(next) => setDrafts((current) => ({ ...current, [story.id]: next }))}
                onDone={() => setEditingId(null)}
                onUndo={() => {
                  setDrafts((current) => {
                    const next = { ...current };
                    delete next[story.id];
                    return next;
                  });
                  setEditingId(null);
                }}
              />
            );
          })}
        </div>
        {!isEditingAny && (
          <AgainBox
            hint={intl.formatMessage(messages.storiesAgainHint)}
            button={intl.formatMessage(messages.storiesAgain)}
            disabled={busy || noRuns}
            onRun={(note) => writeAgain(note)}
          />
        )}
      </>
    );
  }

  return (
    <DirectedStepLayout side={<DirectedSummary />}>
      <div className={shell.head}>
        <h2>{intl.formatMessage(messages.storiesTitle)}</h2>
        <p className={clsx(shell.sub, shell.subDesktop)}>
          {intl.formatMessage(isEditingAny ? messages.storiesEditIntro : messages.storiesIntro, { title: song.title })}
        </p>
      </div>
      <DirectorNotices kind="stories" error={action.error} showRunFailure={!isRevisit} onRetry={() => writeAgain()} />
      {body}
      <div className={shell.foot}>
        <SMLButton className={clsx(styles.small, styles.ghost)} disabled={isWorking} onClick={() => openStep('cast')}>
          {intl.formatMessage(messages.backToCast)}
        </SMLButton>
        <span className={clsx(shell.grow, shell.desktopOnly)} />
        <span className={clsx(shell.footNote, shell.desktopOnly)}>{intl.formatMessage(messages.storiesNext)}</span>
        <span className={styles.phoneOnly}>
          <SMLButton disabled={busy || noRuns || stories.length === 0} onClick={() => writeAgain()}>
            {intl.formatMessage(messages.storiesAgainPhone)}
          </SMLButton>
        </span>
        <SMLButton variant="primary" disabled={busy || picked === null} onClick={onUse}>
          {intl.formatMessage(messages.useStory)}
        </SMLButton>
      </div>
      <ConfirmDialog
        isOpen={isAsking}
        tone="neutral"
        title={intl.formatMessage(messages.changeStoryTitle)}
        description={intl.formatMessage(messages.changeStoryText)}
        confirmLabel={intl.formatMessage(messages.changeStoryConfirm)}
        onConfirm={() => {
          applyStory().catch(console.error);
        }}
        onCancel={() => setIsAsking(false)}
      />
    </DirectedStepLayout>
  );
};

const StoryChoices = TypedMemo(StoryChoicesComponent);

/**
 * Step 2, Story (design directed-video c1 to c4, j2): three ideas of the director. The artist picks
 * one, may change any word of it, or asks for three new ones with a note. "Use this story" sends
 * `PUT …/story` with the picked (maybe edited) story, then `POST …/styles`. A story with a recurring
 * person who has no cast picture opens the cast step instead, with its prompt for the pictures. While the director
 * works, the step shows `DirectorWorking` and every button that starts a run is off.
 */
const StoriesStepComponent: React.FC = () => {
  const { video } = useDirectedVideoContext();
  return <StoryChoices key={choicesKey(video.stories)} />;
};

export const StoriesStep = TypedMemo(StoriesStepComponent);
